import IconExpandMore from '@fortuneo/ui-icons/general/IconExpandMore'
import { useId } from '@hooks/use-id'
import { clsx } from 'clsx'
import React, {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

export type AccordionProps = PropsWithChildren<{
  id: string
  title: string
  expanded?: boolean
  onChange?: (expanded: boolean) => void
}>

export const Accordion: FC<AccordionProps> = ({
  title,
  children,
  onChange,
  expanded: expandedProps,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false)
  const id = useId()
  const regionId = useId()
  const isControlled = expandedProps !== undefined && onChange !== undefined
  const isExpanded = isControlled ? expandedProps : expanded

  const toggle = useCallback(() => {
    if (isControlled) {
      if (onChange) {
        onChange(!expandedProps)
      }
    } else {
      setExpanded((expanded) => !expanded)
    }
  }, [onChange, expandedProps, isControlled])

  useEffect(() => {
    if (onChange && !isControlled) {
      onChange(expanded)
    }
  }, [isControlled, expanded, onChange])

  return (
    <div
      {...props}
      className={clsx(
        'border-b border-solid border-b-[#DDDDDD] fill-amplification-black-premium text-amplification-black-premium',
        'group-[.is-dark-mode]/section-faq:border-b-[#FFFFFF20] group-[.is-dark-mode]/section-faq:fill-[#FFFFFF] group-[.is-dark-mode]/section-faq:text-[#FFFFFF]'
      )}
    >
      <h3>
        <button
          aria-controls={regionId}
          aria-expanded={isExpanded}
          className="flex w-full items-center justify-center active:bg-none"
          id={id}
          type="button"
          onClick={toggle}
        >
          <span className="grow text-balance py-6 text-left text-110 font-normal leading-alternate">
            {title}
          </span>
          <span
            className={clsx(
              'origin-center transform p-[0.25rem] transition duration-200 ease-out',
              { 'rotate-180': isExpanded }
            )}
          >
            <IconExpandMore className="fill-inherit" />
          </span>
        </button>
      </h3>
      <div
        aria-labelledby={id}
        id={regionId}
        role="region"
        className={clsx(
          'grid overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] pb-4 opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden px-[1rem]">{children}</div>
      </div>
    </div>
  )
}
